import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Button, Form, Modal } from "react-bootstrap";
import Card from "../../../components/Card";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DiamondAPI, EtsyAPI } from "../../../js/api";
import { handleApiError } from "../../../js/helper/errorHandler";
import DataTable from "../components/DataTable/DataTable";
import { useTable } from "../components/DataTable/useTable";
import Select from "react-select";

const DiamondSearchSelect = ({ diamondList, value, onSelect, onAddNew }) => {
  const diamondOptions = diamondList.map((d) => ({
    value: String(d._id),
    label: `${d.shape} | ${d.sizeMm}mm | ${d.diamondName} | ${d.carat}ct | ₹${d.rate}`,
    data: d,
  }));

  // ✅ Add custom option at top
  const options = [
    {
      value: "__add_new__",
      label: "➕ Add New Diamond",
      isAddNew: true,
    },
    ...diamondOptions,
  ];

  const selectedOption = options.find((opt) => opt.value === String(value));

  return (
    <div style={{ minWidth: 300 }}>
      <Select
        options={options}
        value={selectedOption || null}
        placeholder="Search diamond..."
        onChange={(selected) => {
          if (!selected) return;

          // ✅ If Add New clicked
          if (selected.isAddNew) {
            onAddNew();
            return;
          }

          // Normal select
          onSelect(selected.data);
        }}
        isClearable
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          option: (base, state) => ({
            ...base,
            fontWeight: state.data?.isAddNew ? "600" : "400",
            color: state.data?.isAddNew ? "#0d6efd" : base.color,
          }),
        }}
      />
    </div>
  );
};

const ProductList = () => {
  const { page, limit, search, setPage, setLimit, setSearch } = useTable({});
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [diamondList, setDiamondList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pushingPrice, setPushingPrice] = useState(false);
  const [showAddDiamondModal, setShowAddDiamondModal] = useState(false);
  const [editedDiamond, setEditedDiamond] = useState(null);
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [newDiamond, setNewDiamond] = useState({
    shape: "",
    diamondName: "",
    clarity: "",
    color: "",
    sizeMm: "",
    carat: "",
    rate: "",
  });

  const columns = [
    { key: "index", label: "No." },
    { key: "title", label: "Title" },
    { key: "status", label: "Status" },
    { key: "variations", label: "Variations" },
    { key: "action", label: "Action" },
  ];

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [listing, setListing] = useState(null);
  const [variations, setVariations] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const fetchDiamondRates = useCallback(async () => {
    try {
      const res = await DiamondAPI.getRates(1, 1000, "");

      if (res.data.status === 200) {
        setDiamondList(res.data.data.rates || []);
      } else {
        handleApiError(res, "Fetching Diamond Rates");
      }
    } catch (err) {
      handleApiError(err);
    }
  }, []);

  useEffect(() => {
    fetchDiamondRates();
  }, [fetchDiamondRates]);

  // ================= FETCH ACCOUNTS =================
  useEffect(() => {
    (async () => {
      try {
        const res = await EtsyAPI.getAccounts();
        if (res.data.status === 200) {
          setAccounts(res.data.data.accounts);
        }
      } catch (err) {
        handleApiError(err);
      }
    })();
  }, []);

  const fetchProducts = useCallback(
    async (account_id, pageNo, pageLimit, keyword) => {
      try {
        setTableLoading(true);

        const res = await EtsyAPI.getProductList(
          account_id,
          pageNo,
          pageLimit,
          keyword,
        );

        if (res.data.status === 200) {
          setProducts(res.data.data.listings);
          setPagination(res.data.data.pagination);
          setShowTable(true);
        }
      } catch (err) {
        handleApiError(err);
      } finally {
        setTableLoading(false);
      }
    },
    [],
  );

  const handleShopChange = (e) => {
    const id = e.target.value;
    setSelectedAccount(id);
    setPage(1);
    setProducts([]);
    setShowTable(false);
  };

  useEffect(() => {
    if (!selectedAccount) return;

    fetchProducts(
      selectedAccount,
      page,
      limit,
      debouncedSearch, // 👈 important
    );
  }, [selectedAccount, page, limit, debouncedSearch, fetchProducts]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 👈 delay you want

    return () => clearTimeout(handler);
  }, [search]);

  // ================= OPEN PRODUCT =================
  const openProduct = async (listing_id) => {
    try {
      const res = await EtsyAPI.getProductByListingId(listing_id);

      if (res.data.status === 200) {
        setListing(res.data.data.listing);
        setVariations(res.data.data.listing.variations || []);
        setShowModal(true);
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  // ================= UPDATE VARIATION =================
  const updateVariation = async () => {
    const confirm = await Swal.fire({
      title: "Update Product?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Update",
    });

    if (!confirm.isConfirmed) return;

    try {
      setSaving(true); // 🔥 START LOADER

      const payload = {
        account_id: selectedAccount,
        diamond_sets: listing.diamond_sets.map((ds) => ({
          type: ds.type,
          diamonds: ds.diamonds.map((d) => ({
            diamond_id: d.diamond_id || d._id,
            qty: d.qty || 1,
          })),
        })),
        variations: variations.map((v) => ({
          _id: v._id,
          gold_grams: v.gold_grams,
        })),
      };

      const res = await EtsyAPI.updateListing(listing.listing_id, payload);

      if (res.data.status === 200) {
        setListing(res.data.data);
        setVariations(res.data.data.variations);
        toast.success("Listing updated successfully");
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false); // 🔥 STOP LOADER
    }
  };

  const handleAddDiamond = async () => {
    try {
      const { shape, diamondName, clarity, color, sizeMm, carat, rate } =
        newDiamond;
      console.log(newDiamond);
      // 🔎 Basic validation
      if (
        !shape ||
        !diamondName ||
        !clarity ||
        !color ||
        !sizeMm ||
        !carat ||
        !rate
      ) {
        toast.error("All fields are required");
        return;
      }

      setSaving(true);

      const res = await DiamondAPI.addRate({
        shape,
        diamondName,
        clarity,
        color,
        sizeMm: sizeMm,
        carat: Number(carat),
        rate: Number(rate),
        currency: "INR",
      });

      if (res.data.status === 200) {
        toast.success("Diamond added successfully!");

        // ✅ Refresh diamond list
        const refresh = await DiamondAPI.getRates(1, 1000, "");
        if (refresh.data.status === 200) {
          setDiamondList(refresh.data.data.rates || []);
        }

        // ✅ Reset form
        setNewDiamond({
          shape: "",
          diamondName: "",
          clarity: "",
          color: "",
          sizeMm: "",
          carat: "",
          rate: "",
        });

        const created = res.data.data;

        // auto assign to edited diamond location
        if (editedDiamond) {
          const copy = { ...listing };
          const { dsIdx, dIdx } = editedDiamond;

          copy.diamond_sets[dsIdx].diamonds[dIdx] = {
            ...created,
            diamond_id: created._id,
            qty: 1,
          };

          setListing(copy);
          setEditedDiamond(null);
        }

        setShowAddDiamondModal(false);
      } else {
        handleApiError(res, "Adding Diamond");
      }
    } catch (err) {
      handleApiError(err, "Adding Diamond");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Body>
              {/* SHOP SELECT */}
              <Row className="mb-3">
                <Col md={8}>
                  <h4>Product List</h4>
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={selectedAccount}
                    onChange={handleShopChange}
                  >
                    <option value="">
                      <i className="fas fa-store"></i> Select Shop
                    </option>
                    {accounts.map((acc) => (
                      <option key={acc._id} value={acc._id}>
                        {acc.shop_name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* EMPTY STATE */}
              {!showTable && !tableLoading && (
                <div className="text-center py-5">
                  <h2 className="text-muted">🛍️ Select shop to see products</h2>
                </div>
              )}

              {/* LOADER */}
              {tableLoading && (
                <div className="text-center py-5">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  />
                  <h5>Loading products...</h5>
                </div>
              )}

              {showTable && !tableLoading && (
                <DataTable
                  columns={columns}
                  data={products}
                  loading={tableLoading}
                  search={search}
                  onSearch={setSearch}
                  page={page}
                  totalPages={pagination?.totalPages || 1}
                  limit={limit}
                  onLimitChange={setLimit}
                  onPageChange={(p) => setPage(p)}
                  renderRow={(p, i) => (
                    <tr key={p._id}>
                      <td>{(page - 1) * limit + i + 1}</td>
                      <td>{p.title}</td>
                      <td>{p.status}</td>
                      <td>{p.raw_data.has_variations ? "Yes" : "No"}</td>
                      <td>
                        <Button
                          size="sm"
                          onClick={() => openProduct(p.listing_id)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="info"
                          size="sm"
                          className="mx-2"
                          href={p.url}
                          target="_blank"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  )}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {listing?.title} - Sku {listing?.variations?.[0]?.sku || "N/A"}{" "}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Card className="mb-3 border-info">
            <Card.Body>
              <strong>How to update this product:</strong>
              <ol className="mb-0 mt-2">
                <li>if diamonds not found then add new diamonds if needed</li>
                <li>Enter gold grams</li>
                <li>
                  Click <b>Save & Recalculate</b>
                </li>
                <li>
                  Click <b>Update Price on Etsy</b>
                </li>
              </ol>
            </Card.Body>
          </Card>

          <Card className="mb-4 p-0">
            <Card.Header className="px-3 pt-3 pb-0">
              <h5 className="mb-0">💎 Diamond Sets</h5>
            </Card.Header>

            <Card.Body className="p-3">
              {listing?.diamond_sets?.map((ds, dsIdx) => (
                <Card key={dsIdx} className="mb-3 border">
                  <Card.Header className="bg-light py-2">
                    <strong>{ds.type}</strong>
                  </Card.Header>

                  <Card.Body>
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Shape</th>
                          <th>Size (mm)</th>
                          <th>Carat / stone</th>
                          <th>Qty</th>
                          <th>Price</th>
                          <th></th>
                        </tr>
                      </thead>

                      <tbody>
                        {ds.diamonds?.map((d, dIdx) => (
                          <tr key={dIdx}>
                            {/* DIAMOND SEARCH */}
                            <td>
                              <DiamondSearchSelect
                                diamondList={diamondList}
                                value={d.diamond_id || d._id}
                                onSelect={(selected) => {
                                  const copy = { ...listing };

                                  copy.diamond_sets[dsIdx].diamonds[dIdx] = {
                                    ...copy.diamond_sets[dsIdx].diamonds[dIdx],
                                    diamond_id: selected._id,
                                    shape: selected.shape,
                                    sizeMm: selected.sizeMm,
                                    carat: selected.carat,
                                    rate: selected.rate,
                                  };

                                  setListing(copy);
                                }}
                                onAddNew={() => setShowAddDiamondModal(true)}
                              />
                            </td>

                            {/* SIZE (readonly) */}
                            <td>
                              <Form.Control
                                size="sm"
                                value={d.sizeMm || ""}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  setListing((prev) => {
                                    const copy = structuredClone(prev);

                                    // store original only first time
                                    if (!editedDiamond) {
                                      setEditedDiamond({
                                        dsIdx,
                                        dIdx,
                                        original: structuredClone(
                                          prev.diamond_sets[dsIdx].diamonds[
                                            dIdx
                                          ],
                                        ),
                                      });
                                    }

                                    copy.diamond_sets[dsIdx].diamonds[
                                      dIdx
                                    ].sizeMm = value;
                                    return copy;
                                  });
                                }}
                              />
                            </td>

                            {/* CARAT (readonly) */}
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                value={d.carat || 0}
                                onChange={(e) => {
                                  const value = Number(e.target.value);

                                  setListing((prev) => {
                                    const copy = structuredClone(prev);

                                    if (!editedDiamond) {
                                      setEditedDiamond({
                                        dsIdx,
                                        dIdx,
                                        original: structuredClone(
                                          prev.diamond_sets[dsIdx].diamonds[
                                            dIdx
                                          ],
                                        ),
                                      });
                                    }

                                    copy.diamond_sets[dsIdx].diamonds[
                                      dIdx
                                    ].carat = value;
                                    return copy;
                                  });
                                }}
                              />
                            </td>

                            {/* QTY */}
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                value={d.qty || 0}
                                onChange={(e) => {
                                  const value = Number(e.target.value);

                                  setListing((prev) => {
                                    const copy = structuredClone(prev);
                                    copy.diamond_sets[dsIdx].diamonds[
                                      dIdx
                                    ].qty = value;
                                    return copy;
                                  });
                                }}
                              />
                            </td>

                            <td>
                              ₹{((d.rate || 0) * (d.qty || 0)).toFixed(2)}
                            </td>

                            {/* REMOVE */}
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  const copy = { ...listing };
                                  copy.diamond_sets[dsIdx].diamonds.splice(
                                    dIdx,
                                    1,
                                  );
                                  setListing(copy);
                                }}
                              >
                                ✕
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="text-end mt-2">
                      <strong>
                        Total Diamond Price: ₹
                        {ds.diamonds
                          ?.reduce(
                            (sum, d) => sum + (d.rate || 0) * (d.qty || 0),
                            0,
                          )
                          .toFixed(2)}
                      </strong>
                    </div>

                    {/* ADD DIAMOND */}
                    {editedDiamond && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => setShowEditConfirmModal(true)}
                      >
                        Confirm Diamond Edit
                      </Button>
                    )}

                    <Button
                      size="sm"
                      className="mx-2"
                      variant="outline-primary"
                      onClick={() => {
                        const copy = { ...listing };
                        copy.diamond_sets[dsIdx].diamonds.push({
                          shape: "",
                          size_mm: "",
                          carat: 0,
                          qty: 1,
                          id: "",
                        });
                        setListing(copy);
                      }}
                    >
                      ➕ Add Diamond
                    </Button>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>
          <Card className="mb-3">
            <Card.Body>
              <Row>
                <Col>
                  <strong>Total Variations:</strong> {variations.length}
                </Col>
                <Col>
                  <strong>Min Price:</strong> ₹
                  {Math.min(...variations.map((v) => v.current_price))}
                </Col>
                <Col>
                  <strong>Max Price:</strong> ₹
                  {Math.max(...variations.map((v) => v.current_price))}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* VARIATIONS TABLE */}
          <DataTable
            columns={[
              { key: "sku", label: "SKU" },
              { key: "size", label: "Size" }, // NEW
              { key: "type", label: "Diamond Type" },
              { key: "gold", label: "Gold Purity" },
              { key: "gold_grams", label: "Gold (g)" },
              { key: "price", label: "Price" },
            ]}
            data={variations}
            showSearch={false}
            showLimit={false}
            showPagination={false}
            renderRow={(v, idx) => (
              <tr key={v._id}>
                <td>{v.sku}</td>
                <td>
                  {v.size_type ? (
                    <span>
                      <strong>{v.size_type}:</strong> {v.size_value}
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>{v.diamond_type}</td>
                <td>{v.gold_purity}</td>

                <td>
                  <Form.Control
                    type="number"
                    value={v.gold_grams || 0}
                    onChange={(e) => {
                      const copy = [...variations];
                      copy[idx].gold_grams = Number(e.target.value);
                      setVariations(copy);
                    }}
                  />
                </td>

                <td>
                  <div style={{ lineHeight: "1.2" }}>
                    <small className="text-muted">Old: ₹{v.base_price}</small>
                    <div
                      style={{
                        fontSize: "1.05rem",
                        fontWeight: "600",
                        color:
                          v.current_price > v.base_price
                            ? "#198754"
                            : "#0d6efd",
                      }}
                    >
                      New: ₹{v.current_price}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          />

          <Button variant="success" onClick={updateVariation} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              "Save & Recalculate All"
            )}
          </Button>

          <Button
            className="mx-2"
            disabled={pushingPrice}
            onClick={async () => {
              try {
                setPushingPrice(true); // 🔥 START LOADER

                const res = await EtsyAPI.pushListingPrice(
                  listing.listing_id,
                  selectedAccount,
                );

                if (res.data.status === 200) {
                  toast.success("Price updated on Etsy");

                  setShowModal(false); // ✅ CLOSE MODAL AFTER SUCCESS
                }
              } catch (err) {
                handleApiError(err);
              } finally {
                setPushingPrice(false); // 🔥 STOP LOADER
              }
            }}
          >
            {pushingPrice ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Updating...
              </>
            ) : (
              "Update Price on Etsy"
            )}
          </Button>
        </Modal.Body>
      </Modal>

      <Modal
        show={showAddDiamondModal}
        onHide={() => setShowAddDiamondModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Diamond</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {Object.keys(newDiamond).map((field) => (
            <Form.Group className="mb-2" key={field}>
              <Form.Label>{field}</Form.Label>
              <Form.Control
                type={field === "rate" || field === "carat" ? "number" : "text"}
                value={newDiamond[field]}
                onChange={(e) =>
                  setNewDiamond({
                    ...newDiamond,
                    [field]: e.target.value,
                  })
                }
              />
            </Form.Group>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddDiamondModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleAddDiamond}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              "Save Diamond"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showEditConfirmModal}
        onHide={() => setShowEditConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Diamond Edited</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Do you want to use original diamond or create a new one?</p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setListing((prev) => {
                const copy = structuredClone(prev);
                const { dsIdx, dIdx, original } = editedDiamond;

                copy.diamond_sets[dsIdx].diamonds[dIdx] = original;

                return copy;
              });

              setEditedDiamond(null);
              setShowEditConfirmModal(false);
            }}
          >
            Use Original Diamond
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              if (!editedDiamond) return;

              const { dsIdx, dIdx } = editedDiamond;

              // get current edited diamond from listing state
              const currentDiamond =
                listing?.diamond_sets?.[dsIdx]?.diamonds?.[dIdx];

              if (!currentDiamond) return;

              // find full diamond data if exists
              const fullDiamond = diamondList.find(
                (d) => d._id === currentDiamond.diamond_id,
              );

              setNewDiamond({
                shape: currentDiamond.shape ?? fullDiamond?.shape ?? "",
                diamondName: fullDiamond?.diamondName ?? "",
                clarity: fullDiamond?.clarity ?? "",
                color: fullDiamond?.color ?? "",
                sizeMm: currentDiamond.sizeMm ?? fullDiamond?.sizeMm ?? "",
                carat: currentDiamond.carat ?? fullDiamond?.carat ?? "",
                rate: currentDiamond.rate ?? fullDiamond?.rate ?? "",
              });

              setShowEditConfirmModal(false);
              setShowAddDiamondModal(true);
            }}
          >
            Create New Diamond
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductList;
